import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface FeatureGroup {
  id: string
  name: string
  features: number
  freshness: string
  storage: string
  requests_day: number
}

interface Feature {
  name: string
  group: string
  type: string
  stats: Record<string, number>
  models_using: number
}

interface Statistics {
  total_features: number
  feature_groups: number
  active_features: number
  feature_freshness_pct: number
  daily_feature_requests: number
  avg_latency_ms: number
}

export default function FeatureStorePage() {
  const [groups, setGroups] = useState<FeatureGroup[]>([])
  const [features, setFeatures] = useState<Feature[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [groupsRes, featuresRes, statsRes] = await Promise.all([
        fetch('/api/featurestore/groups'),
        fetch('/api/featurestore/features'),
        fetch('/api/featurestore/statistics')
      ])
      if (groupsRes.ok) setGroups(await groupsRes.json())
      if (featuresRes.ok) setFeatures(await featuresRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching feature store:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Feature Store</h1>
          <p className="mt-2 text-gray-600">Phase 275 - Feature management and serving</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Features</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_features)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Feature Groups</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.feature_groups}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Features</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_features}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Freshness</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.feature_freshness_pct}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Daily Requests</p>
              <p className="text-2xl font-bold text-orange-600">{formatNumber(statistics.daily_feature_requests)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Latency</p>
              <p className="text-2xl font-bold text-green-600">{statistics.avg_latency_ms}ms</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Feature Groups</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((group) => (
              <div key={group.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{group.name}</span>
                  <span className="text-lg font-bold text-blue-600">{group.features}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Freshness</p>
                    <p className="font-medium text-green-600">{group.freshness}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Storage</p>
                    <p className="font-medium text-purple-600">{group.storage}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">{formatNumber(group.requests_day)} requests/day</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Features</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feature</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Models Using</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {features.map((feat, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 font-mono text-blue-600">{feat.name}</td>
                    <td className="px-6 py-4 text-gray-900">{feat.group}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">{feat.type}</span>
                    </td>
                    <td className="px-6 py-4 text-green-600 font-medium">{feat.models_using}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
